import { Box, Flex, Heading, Spacer, Stack, Tag, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { DateTime } from 'luxon'

import getPlainText from '@/client/services/richText/getPlainText'

import HostAvatars from '@/components/Host/HostAvatars'
import { toRegistrationState } from '@/components/RegistrationState/helpers'
import RegistrationState from '@/components/RegistrationState/RegistrationState'
import SessionDateTooltip from '@/components/Tooltips/SessionDateTooltip'
import LiveNowTag from '@/components/UI/LiveNowTag/LiveNowTag'
import RelativeTime from '@/components/UI/RelativeTime/RelativeTime'
import SoftLink from '@/components/UI/SoftLink'

import type { SessionBoxProps } from './types'

const SessionBox: React.FC<
  SessionBoxProps & {
    hideEntrypoint?: boolean
    showBorder?: boolean
    hideDescription?: boolean
  }
> = ({
  startDate,
  endDate,
  title,
  topicName,
  description,
  link,
  countRegistrations,
  maxLearners,
  isUserRegistered,
  isUserSelfReserved,
  hosts,
  numHosts,
  attendancePolicy,
  hideEntrypoint = false,
  showBorder = false,
  hideDescription = false,
}) => {
  const sessionStartDateTime = DateTime.fromISO(startDate)
  const sessionEndDateTime = DateTime.fromISO(endDate)
  const inProgress =
    sessionStartDateTime <= DateTime.local() && sessionEndDateTime > DateTime.local()
  const hasEnded = sessionEndDateTime < DateTime.local()

  const component = (
    <Flex
      flexDirection="column"
      borderRadius="8px"
      minHeight={hideDescription ? '200px' : '280px'}
      border={showBorder ? '2px solid' : 'none'}
      borderColor={showBorder ? 'border.emphasized' : undefined}
    >
      <Box p="24px" borderTop="10px solid" borderTopColor="border" borderTopRadius="6px">
        <Box minHeight="80px">
          <Heading size="xl" as="h3" mb="4px" noOfLines={2} fontSize="20px">
            {title ?? topicName}
          </Heading>
          {attendancePolicy === 'required' ? <Tag colorScheme="yellow">REQUIRED</Tag> : null}
          <Wrap alignItems="center">
            {inProgress ? (
              <WrapItem>
                <LiveNowTag />
              </WrapItem>
            ) : null}
            <WrapItem>
              <SessionDateTooltip
                sessionStartDateTime={sessionStartDateTime}
                sessionEndDateTime={sessionEndDateTime}
              >
                <Text fontWeight="bold" color="fg.muted">
                  <RelativeTime time={startDate} includeWeekday />
                </Text>
              </SessionDateTooltip>
            </WrapItem>
          </Wrap>
        </Box>
        {hideDescription ? null : (
          <Text noOfLines={3} mt="8px">
            {getPlainText(description)}
          </Text>
        )}
      </Box>
      <Spacer />
      <Box px="24px" py="16px" borderTop="1px solid" borderColor="border">
        <Stack direction="row" spacing={2} justify="space-between" align="center">
          <HostAvatars hosts={hosts} numHosts={numHosts} isSession={false} isTooltip size="sm" />
          <RegistrationState
            state={toRegistrationState({ hasEnded, isUserRegistered, isUserSelfReserved })}
            numRegistrations={countRegistrations}
            maxLearners={maxLearners}
            isSeries={false}
          />
        </Stack>
      </Box>
    </Flex>
  )

  return hideEntrypoint ? component : <SoftLink href={link}>{component}</SoftLink>
}

export default SessionBox
